// src/AuthModal.tsx
import { useEffect, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from './AuthContext';
import { ApiError } from './auth';
import { isBenignOAuthError, describeOAuthError } from './oauthErrors';
import './AuthModal.css';

type Step = 'email' | 'join' | 'code';
// 'join' (Google sign-in, no account yet) needs the unsaved ID token, so it's never restored.
const RESTORABLE_STEPS: readonly Step[] = ['email', 'code'];

/**
 * Survives a remount (Header only renders AuthModal while shown, so closing
 * it — even by mistake — would otherwise wipe all progress). Without this, a
 * host who already has a code in their inbox gets bounced back to the email
 * step on reopen, whose submit handler unconditionally re-requests a code
 * and immediately hits sendCode.ts's 60s resend cooldown (429) — no field to
 * enter the code they already have. Cleared once sign-in/registration
 * actually succeeds, or explicitly via "Use a different email".
 */
interface Progress {
  step: Step;
  mode: 'signin' | 'register';
  email: string;
}

// Mirrors src/firebase.ts's config check without importing it — the Firebase
// SDK is a lazy chunk so it stays out of the main bundle.
const isOAuthConfigured = !!(import.meta.env.VITE_FIREBASE_API_KEY && import.meta.env.VITE_FIREBASE_AUTH_DOMAIN);

// Preloaded when the modal mounts (not on click): signInWithPopup has to run
// while the click's user activation is still live, or browsers (Safari
// especially) block the popup — so handleGoogle must not wait on a download.
let firebaseModule: typeof import('./firebase') | null = null;
let firebaseLoad: Promise<typeof import('./firebase')> | null = null;
function loadFirebase() {
  firebaseLoad ??= import('./firebase').then(m => {
    m.prepareGoogleSignIn();
    return (firebaseModule = m);
  });
  return firebaseLoad;
}

const PROGRESS_KEY = 'shelfkin-auth-modal-progress';

function loadProgress(): Progress | null {
  try {
    const raw = sessionStorage.getItem(PROGRESS_KEY);
    const progress = raw ? JSON.parse(raw) as Progress : null;
    // A step name from an older build (e.g. the retired 'penName') starts over.
    return progress && RESTORABLE_STEPS.includes(progress.step) ? progress : null;
  } catch { return null; }
}

function clearProgress() {
  try { sessionStorage.removeItem(PROGRESS_KEY); } catch { /* ignore */ }
}

export function AuthModal({ onClose }: { onClose: () => void }) {
  const { sendCode, signIn, register, oauthSignIn, oauthRegister, authLoading } = useAuth();
  const [saved] = useState(loadProgress);

  const [step, setStep]       = useState<Step>(saved?.step ?? 'email');
  const [mode, setMode]       = useState<'signin' | 'register'>(saved?.mode ?? 'signin');
  const [email, setEmail]     = useState(saved?.email ?? '');
  const [code, setCode]       = useState('');
  const [error, setError]     = useState<string | null>(null);
  // Set only when the join step was reached via Google sign-in (a verified
  // email with no account yet) — that step then registers with this token
  // instead of sending an OTP. Deliberately not persisted with Progress: the
  // ID token is short-lived, and a reopened modal can just redo the popup.
  const [oauthIdToken, setOauthIdToken] = useState<string | null>(null);
  const [googleBusy, setGoogleBusy]     = useState(false);

  useEffect(() => {
    if (isOAuthConfigured) loadFirebase().catch(() => { firebaseLoad = null; /* retried on click */ });
  }, []);

  useEffect(() => {
    if (step === 'email' || oauthIdToken) { clearProgress(); return; }
    const progress: Progress = { step, mode, email };
    try { sessionStorage.setItem(PROGRESS_KEY, JSON.stringify(progress)); } catch { /* ignore */ }
  }, [step, mode, email, oauthIdToken]);

  function startOver() {
    clearProgress();
    setStep('email');
    setMode('signin');
    setCode('');
    setOauthIdToken(null);
    setError(null);
  }

  async function handleGoogle() {
    setError(null);
    setGoogleBusy(true);
    try {
      const { signInWithGoogle } = firebaseModule ?? await loadFirebase();
      const idToken = await signInWithGoogle();
      const result = await oauthSignIn(idToken);
      if (result) {
        // Verified by Google, but no account yet — confirm, then register with the same token.
        setEmail(result.email);
        setOauthIdToken(idToken);
        setMode('register');
        setStep('join');
        return;
      }
      clearProgress();
      onClose();
    } catch (err) {
      const code = (err as { code?: string } | null)?.code;
      if (isBenignOAuthError(code)) return; // user dismissed the popup or triggered a second one
      setError(describeOAuthError(code) ?? (err instanceof Error ? err.message : 'Sign-in failed. Please try again.'));
    } finally {
      setGoogleBusy(false);
    }
  }

  async function handleEmailSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await sendCode(email, 'signin');
      setMode('signin');
      setStep('code');
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // Not registered yet — send a registration code instead; the code step
        // says there's no account yet, so a mistyped email is easy to spot.
        try {
          await sendCode(email, 'register');
          setMode('register');
          setStep('code');
        } catch (registerErr) {
          setError(registerErr instanceof Error ? registerErr.message : 'Something went wrong. Please try again.');
        }
      } else {
        setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      }
    }
  }

  /** Google sign-in with no account yet: create it with the same token. */
  async function handleJoinSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!oauthIdToken) { startOver(); return; }
    try {
      await oauthRegister(oauthIdToken);
      clearProgress();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // The Google ID token expired (they lingered on this step for over an
        // hour) — resubmitting it can never succeed, so send them back to redo
        // the popup.
        setOauthIdToken(null);
        setMode('signin');
        setStep('email');
        setError('Your Google sign-in expired. Please continue with Google again.');
        return;
      }
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  }

  async function handleCodeSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (mode === 'signin') {
        await signIn(email, code);
      } else {
        await register(email, code);
      }
      clearProgress();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  }

  async function handleResend() {
    setError(null);
    try {
      await sendCode(email, mode);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code.');
    }
  }

  return createPortal(
    // No backdrop-click-to-dismiss: this modal holds in-progress state (an
    // already-sent code, a filled-out registration form) that's expensive to
    // lose to a stray click — see the Progress persistence above. Only the ×
    // button and a successful submit close it.
    <div className="auth-modal-backdrop">
      <div className="auth-modal">
        <button className="auth-modal-close" onClick={onClose} aria-label="Close">×</button>

        {step === 'email' && (
          <form onSubmit={handleEmailSubmit}>
            <h2>Sign in / Join Shelfkin</h2>
            <p className="auth-modal-sub">We'll email you a one-time code — no password to remember.</p>
            <input
              type="email"
              required
              autoFocus
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            {error && <p className="auth-modal-error">{error}</p>}
            <button type="submit" disabled={authLoading || googleBusy}>Continue</button>
            {isOAuthConfigured && (
              <>
                <div className="auth-modal-divider"><span>or</span></div>
                <button
                  type="button" className="auth-modal-oauth"
                  onClick={handleGoogle} disabled={authLoading || googleBusy}
                >
                  {googleBusy ? 'Connecting…' : 'Continue with Google'}
                </button>
              </>
            )}
          </form>
        )}

        {step === 'join' && (
          <form onSubmit={handleJoinSubmit}>
            <h2>Create your Shelfkin account</h2>
            <p className="auth-modal-sub">
              Signed in with Google as <strong>{email}</strong>.
              Every new account is reviewed by hand — we'll email you once you're approved.
            </p>
            {error && <p className="auth-modal-error">{error}</p>}
            {/* No autoFocus: a held Enter from the Google popup shouldn't confirm this for them. */}
            <button type="submit" disabled={authLoading}>Create account</button>
            <button type="button" className="auth-modal-link" onClick={startOver} disabled={authLoading}>
              Use a different account
            </button>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={handleCodeSubmit}>
            <h2>Check your inbox</h2>
            {mode === 'register' ? (
              <p className="auth-modal-sub">
                There's no account for <strong>{email}</strong> yet — enter the 6-digit code we sent to create one.
                Every new account is reviewed by hand — we'll email you once you're approved.
              </p>
            ) : (
              <p className="auth-modal-sub">Enter the 6-digit code we sent to {email}.</p>
            )}
            <input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              required
              autoFocus
              placeholder="123456"
              value={code}
              onChange={e => setCode(e.target.value)}
            />
            {error && <p className="auth-modal-error">{error}</p>}
            <button type="submit" disabled={authLoading}>
              {mode === 'register' ? 'Create account' : 'Sign in'}
            </button>
            <button type="button" className="auth-modal-link" onClick={handleResend} disabled={authLoading}>
              Resend code
            </button>
            <button type="button" className="auth-modal-link" onClick={startOver} disabled={authLoading}>
              Use a different email
            </button>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}
